import { useCallback, useState, useMemo } from "react";
import classNames from "classnames";
import Select, {
	ActionMeta,
	GroupBase,
	Props as ReactSelectProps,
	SingleValue,
} from "react-select";
import {
	DropdownIndicator,
	Menu,
	Option,
} from "app/pages/.shared/form/AdvancedSelectFormik/componentsOverrides";
import "app/pages/.shared/form/AdvancedSelectFormik/AdvancedSelectFormik.scss";
import { SearchOption } from "../../types";
import LocationMenuList from "app/pages/SmartDP/Search/SDPSearchForm/LocationMenuList";
import LocationMenuChip from "app/pages/SmartDP/Search/SDPSearchForm/LocationMenuChip";
import InputValueFormat from "app/pages/SmartDP/Search/SDPSearchForm/InputValueFormat";
import ClearIndicatorSdpSearchInput from "app/pages/SmartDP/Search/SDPSearchForm/ClearIndicatorSdpSearchInput";
import TopOptionChip from "app/pages/SmartDP/Search/SDPSearchForm/TopOptionChip/TopOptionChip";
import IconDeparting from "app/pages/.shared/IconDeparting";
import Typography, { TYPOGRAPHY_VARIANTS } from "app/pages/.shared/Typography/Typography";
import { smartDPDestinationSuggestion } from "app/utils/smartDPUtils";
import IconLocation from "app/pages/.shared/IconLocation";
import { StepId } from "../../types/StepId";
import { useIntl } from "react-intl";
import "./LocationCombobox.scss";
import { useDebouncedFuzzySearch } from "app/hooks";
import { SEARCH_FUZZY_THRESHOLD } from "app/constants";
import {
	filterAndSortDepartureCities as sortDepartureCities,
	sortOptions as sortDestinations,
} from "app/utils/utils";

type LocationComboboxChangeMeta = {
	showChipMenu: boolean;
	actionMeta: ActionMeta<SearchOption>;
};

type LocationComboboxProps = {
	id: Exclude<StepId, "travelDates">;
	label: string;
	options: SearchOption[];
	optionsWhenEmpty?: SearchOption[];
	onChange?: (option: SingleValue<SearchOption>, meta: LocationComboboxChangeMeta) => void;
} & Omit<ReactSelectProps<SearchOption, false, GroupBase<SearchOption>>, "options" | "onChange">;

const getOptionLabelText = (option: SearchOption): string => {
	if (typeof option === "string") {
		return option;
	}
	if (option && typeof option === "object" && "label" in option) {
		return String(option.label);
	}
	return "";
};

const renderDepartureOption = (label: string) => (
	<div className="search-form__suggestion">
		<div className="search-form__suggestion-picto">
			<IconDeparting />
		</div>

		<Typography
			className="search-form__suggestion-first-label"
			variant={TYPOGRAPHY_VARIANTS.REGULAR}
		>
			{label}
		</Typography>
	</div>
);

const renderDestinationOption = (option: SearchOption) => {
	const labels =
		typeof option === "object" && option && "labels" in option && Array.isArray(option.labels)
			? option.labels
			: [];
	const levels =
		typeof option === "object" && option && "levels" in option && Array.isArray(option.levels)
			? option.levels
			: [];
	const firstSuggestion = labels.slice(-1).join(", ");
	const secondSuggestion = smartDPDestinationSuggestion(firstSuggestion, levels);
	const firstLine = firstSuggestion || getOptionLabelText(option);

	return (
		<div className="search-form__suggestion">
			<div className="search-form__suggestion-picto">
				<IconLocation style={{ marginLeft: "2px" }} />
			</div>
			<div className="search-form__suggestion-text">
				<Typography
					className="search-form__suggestion-first-label"
					variant={TYPOGRAPHY_VARIANTS.REGULAR}
				>
					{firstLine}
				</Typography>

				{secondSuggestion && (
					<span className="search-form__suggestion-second-label">{secondSuggestion}</span>
				)}
			</div>
		</div>
	);
};

const SORT_STRATEGY = {
	departureCity: sortDepartureCities,
	destinationResort: sortDestinations,
};

export const LocationCombobox = ({
	id,
	value,
	options,
	optionsWhenEmpty,
	className,
	label,
	placeholder: placeholderProp = "",
	isClearable = true,
	onChange,
	...rest
}: LocationComboboxProps) => {
	const { formatMessage } = useIntl();

	const [inputValue, setInputValue] = useState("");
	const [isFocused, setIsFocused] = useState(false);

	const fuzzyOptions = useDebouncedFuzzySearch(options, inputValue, {
		// @ts-expect-error TODO: corriger cette erreur, keys n'est pas défini dans les options de useDebouncedFuzzySearch
		keys: id === "departureCity" ? ["label"] : ["labels"],
		fuseOptions: { threshold: SEARCH_FUZZY_THRESHOLD },
	});

	const filteredOptions = useMemo(() => {
		return SORT_STRATEGY[id](options, fuzzyOptions, inputValue);
	}, [options, fuzzyOptions, inputValue, id]);

	const optionsWhenEmptyCount = optionsWhenEmpty === undefined ? 0 : optionsWhenEmpty.length;
	const showChipMenu = inputValue.trim().length === 0 && optionsWhenEmptyCount > 0;

	const computedOptions = showChipMenu ? optionsWhenEmpty : filteredOptions;
	const computedMenuList = showChipMenu ? LocationMenuChip : LocationMenuList;

	const isFloating = isFocused || Boolean(value) || inputValue.trim().length > 0;
	const placeholder = isFloating ? placeholderProp : "";

	const rootClassName = classNames("location-combobox", "advanced-select", className);

	const formatOptionLabel = (option: SearchOption) => {
		if (showChipMenu) {
			return <TopOptionChip label={getOptionLabelText(option)} />;
		}
		return id === "destinationResort"
			? renderDestinationOption(option)
			: renderDepartureOption(getOptionLabelText(option));
	};

	const handleInputChange = useCallback((newVal: string) => {
		setInputValue(newVal);
	}, []);

	const getOptionValue = useCallback((option: SearchOption) => {
		return typeof option === "object" ? String(option.code) : String(option);
	}, []);

	const getOptionLabel = useCallback((option: SearchOption) => {
		return typeof option === "object" ? String(option.label) : String(option);
	}, []);

	const handleChange = useCallback(
		(option: SingleValue<SearchOption>, actionMeta: ActionMeta<SearchOption>) => {
			if (onChange) {
				onChange(option, { showChipMenu, actionMeta });
			}
		},
		[onChange, showChipMenu]
	);

	return (
		<div className={rootClassName}>
			<Select
				value={value}
				options={computedOptions}
				onInputChange={handleInputChange}
				isClearable={isClearable}
				components={{
					Menu,
					Option,
					DropdownIndicator,
					ClearIndicator: ClearIndicatorSdpSearchInput,
					SingleValue: InputValueFormat,
					// @ts-expect-error TODO: fix this error, LocationMenuChip is not compatible with MenuListProps
					MenuList: computedMenuList,
				}}
				classNamePrefix="advanced-select__select"
				menuIsOpen={true}
				getOptionValue={getOptionValue}
				getOptionLabel={getOptionLabel}
				onFocus={() => setIsFocused(true)}
				onBlur={() => setIsFocused(false)}
				inputId={id}
				placeholder={placeholder}
				formatOptionLabel={formatOptionLabel}
				onChange={handleChange}
				filterOption={() => true}
				{...showChipMenu && {
					locationMenuLabel: formatMessage({
						id: "sdp.search.top.destinations.cities.label",
					}),
				}}
				{...rest}
			/>

			<label
				className={classNames("location-combobox__label", {
					"location-combobox__label--floating": isFloating,
				})}
				htmlFor={id}
			>
				{label}
			</label>
		</div>
	);
};
